import createDecorator from 'final-form-calculate'
import _ from 'lodash'
import * as yup from 'yup'
import { numberToDollars } from '../../../utils'

// Validation object
export const schema = (maxRothPreTaxPercentContribution, maxRothPreTaxDollarContribution) =>
  yup
    .object({
      contributionEligibility: yup.boolean().nullable().required('This field is required'),
      employeeContrib: yup
        .boolean()
        .nullable()
        .when('contributionEligibility', ([contributionEligibility], schema) =>
          contributionEligibility ? schema.required('This field is required') : schema
        ),
      contribMethod: yup
        .string()
        .nullable()
        .when('employeeContrib', ([employeeContrib], schema) =>
          employeeContrib ? schema.required('This field is required') : schema
        ),
      pretaxSavingsRate: yup
        .number()
        .nullable()
        .min(0, `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`)
        .max(
          maxRothPreTaxPercentContribution,
          `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`
        )
        .when(['employeeContrib', 'contribMethod'], ([employeeContrib, contribMethod], schema) =>
          employeeContrib && contribMethod === 'P'
            ? schema.required('This field is required')
            : schema
        )
        .test('percent-sum-limit-preTaxRate', function (value) {
          const percentLimit = Number(maxRothPreTaxPercentContribution)
          const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
          if (this.parent.contribMethod !== 'P' || !hasLimit) return true
          const { rothSavingsRate, posttaxSavingsRate } = this.parent || {}
          const preTaxRateEntered = Number(value) || 0
          const rothRateEntered = Number(rothSavingsRate) || 0
          const afterTaxRateEntered = Number(posttaxSavingsRate) || 0
          const withinPercentLimit =
            preTaxRateEntered + rothRateEntered + afterTaxRateEntered <= percentLimit
          return (
            withinPercentLimit ||
            this.createError({ message: `Total contribution rate cannot exceed ${percentLimit}%` })
          )
        }),
      preTaxSavingsAmt: yup
        .number()
        .nullable()
        .min(0, `Please enter an amount between $0 and $${maxRothPreTaxDollarContribution}`)
        .max(
          maxRothPreTaxDollarContribution,
          `Please enter an amount between $0 and $${maxRothPreTaxDollarContribution}`
        )
        .when(['employeeContrib', 'contribMethod'], ([employeeContrib, contribMethod], schema) =>
          employeeContrib && contribMethod === 'D'
            ? schema.required('This field is required')
            : schema
        )
        .test('dollar-sum-limit-preTaxAmount', function (value) {
          const hasLimit =
            Number.isFinite(maxRothPreTaxDollarContribution) && maxRothPreTaxDollarContribution > 0
          if (this.parent.contribMethod !== 'D' || !hasLimit) return true
          const dollarLimit = maxRothPreTaxDollarContribution
          const { rothSavingsAmt, postTaxSavingsAmt } = this.parent || {}
          const preTaxAmountEntered = Number(value) || 0
          const rothAmountEntered = Number(rothSavingsAmt) || 0
          const afterTaxAmountEntered = Number(postTaxSavingsAmt) || 0
          const withinDollarLimit =
            preTaxAmountEntered + rothAmountEntered + afterTaxAmountEntered <= dollarLimit
          return (
            withinDollarLimit ||
            this.createError({
              message: `Total contribution amount cannot exceed $${numberToDollars(dollarLimit, true)}`,
            })
          )
        }),
      rothContribAllowed: yup.boolean().nullable().required('This field is required'),
      rothSavingsRate: yup
        .number()
        .nullable()
        .min(0, `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`)
        .max(
          maxRothPreTaxPercentContribution,
          `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`
        )
        .when(
          ['employeeContrib', 'rothContribAllowed', 'contribMethod'],
          ([employeeContrib, rothContribAllowed, contribMethod], schema) =>
            employeeContrib && rothContribAllowed && contribMethod === 'P'
              ? schema.required('This field is required')
              : schema
        )
        .test('percent-sum-limit-rothRate', function (value) {
          const percentLimit = Number(maxRothPreTaxPercentContribution)
          const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
          if (this.parent.contribMethod !== 'P' || !hasLimit) return true
          const { pretaxSavingsRate, posttaxSavingsRate } = this.parent || {}
          const rothRateEntered = Number(value) || 0
          const preTaxRateEntered = Number(pretaxSavingsRate) || 0
          const afterTaxRateEntered = Number(posttaxSavingsRate) || 0
          const withinPercentLimit =
            rothRateEntered + preTaxRateEntered + afterTaxRateEntered <= percentLimit
          return (
            withinPercentLimit ||
            this.createError({ message: `Total contribution rate cannot exceed ${percentLimit}%` })
          )
        }),
      rothSavingsAmt: yup
        .number()
        .nullable()
        .min(0, `Please enter an amount between $0 and $${maxRothPreTaxDollarContribution}`)
        .max(
          maxRothPreTaxDollarContribution,
          `Please enter an amount between $0 and $${maxRothPreTaxDollarContribution}`
        )
        .when(
          ['employeeContrib', 'rothContribAllowed', 'contribMethod'],
          ([employeeContrib, rothContribAllowed, contribMethod], schema) =>
            employeeContrib && rothContribAllowed && contribMethod === 'D'
              ? schema.required('This field is required')
              : schema
        )
        .test('dollar-sum-limit-rothAmount', function (value) {
          const hasLimit =
            Number.isFinite(maxRothPreTaxDollarContribution) && maxRothPreTaxDollarContribution > 0

          if (this.parent.contribMethod !== 'D' || !hasLimit) return true
          const dollarLimit = maxRothPreTaxDollarContribution
          const { preTaxSavingsAmt, postTaxSavingsAmt } = this.parent || {}
          const rothAmountEntered = Number(value) || 0
          const preTaxAmountEntered = Number(preTaxSavingsAmt) || 0
          const afterTaxAmountEntered = Number(postTaxSavingsAmt) || 0
          const withinDollarLimit =
            rothAmountEntered + preTaxAmountEntered + afterTaxAmountEntered <= dollarLimit
          return (
            withinDollarLimit ||
            this.createError({
              message: `Total contribution amount cannot exceed $${numberToDollars(dollarLimit, true)}`,
            })
          )
        }),
      posttaxContribAllowed: yup.boolean().nullable().required('This field is required'),
      posttaxSavingsRate: yup
        .number()
        .nullable()
        .min(0, `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`)
        .max(
          maxRothPreTaxPercentContribution,
          `Please enter a value between 0 and ${maxRothPreTaxPercentContribution}`
        )
        .when(
          ['employeeContrib', 'posttaxContribAllowed', 'contribMethod'],
          ([employeeContrib, posttaxContribAllowed, contribMethod], schema) =>
            employeeContrib && posttaxContribAllowed && contribMethod === 'P'
              ? schema.required('This field is required')
              : schema
        )
        .test('percent-sum-limit-afterTaxRate', function (value) {
          const percentLimit = Number(maxRothPreTaxPercentContribution)
          const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
          if (this.parent.contribMethod !== 'P' || !hasLimit) return true
          const { pretaxSavingsRate, rothSavingsRate } = this.parent || {}
          const postTaxRateEntered = Number(value) || 0
          const preTaxRateEntered = Number(pretaxSavingsRate) || 0
          const rothRateEntered = Number(rothSavingsRate) || 0
          const withinPercentLimit =
            postTaxRateEntered + preTaxRateEntered + rothRateEntered <= percentLimit
          return (
            withinPercentLimit ||
            this.createError({ message: `Total contribution rate cannot exceed ${percentLimit}%` })
          )
        }),
      postTaxSavingsAmt: yup
        .number()
        .nullable()
        .min(0, 'Please enter an amount between $0 and $100,000,000')
        .max(100000000, 'Please enter an amount between $0 and $100,000,000')
        .when(
          ['employeeContrib', 'posttaxContribAllowed', 'contribMethod'],
          ([employeeContrib, posttaxContribAllowed, contribMethod], schema) =>
            employeeContrib && posttaxContribAllowed && contribMethod === 'D'
              ? schema.required('This field is required')
              : schema
        )
        .test('dollar-sum-limit-afterTaxAmount', function (value) {
          const hasLimit =
            Number.isFinite(maxRothPreTaxDollarContribution) && maxRothPreTaxDollarContribution > 0
          if (this.parent.contribMethod !== 'D' || !hasLimit) return true
          const dollarLimit = maxRothPreTaxDollarContribution
          const { rothSavingsAmt, preTaxSavingsAmt } = this.parent || {}
          const afterTaxAmountEntered = Number(value) || 0
          const rothAmountEntered = Number(rothSavingsAmt) || 0
          const preTaxAmountEntered = Number(preTaxSavingsAmt) || 0
          const withinDollarLimit =
            afterTaxAmountEntered + rothAmountEntered + preTaxAmountEntered <= dollarLimit
          return (
            withinDollarLimit ||
            this.createError({
              message: `Total contribution amount cannot exceed $${numberToDollars(dollarLimit, true)}`,
            })
          )
        }),
    })
    .test('at-least-one-rate', 'Please enter an amount', function (values) {
      if (values.employeeContrib && values.contribMethod === 'P') {
        const pre = Number(values.pretaxSavingsRate) ?? 0
        const roth = Number(values.rothSavingsRate) ?? 0
        const post = Number(values.posttaxSavingsRate) ?? 0
        return pre > 0 || roth > 0 || post > 0
      }
      return true
    })
    .test('at-least-one-amt', 'Please enter an amount', function (values) {
      if (values.employeeContrib && values.contribMethod === 'D') {
        const pre = Number(values.preTaxSavingsAmt) ?? 0
        const roth = Number(values.rothSavingsAmt) ?? 0
        const post = Number(values.postTaxSavingsAmt) ?? 0
        return pre > 0 || roth > 0 || post > 0
      }
      return true
    })

// The keys to patch to API
export const accountTypeAllowedKeys = {
  '401a': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  '401k': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  '403b': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  '457': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  'keogh': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  'other-asset': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  'payroll-deduct-ira': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
  'sep-ira': [
    'contributionEligibility',
    'employerContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
  ],
  'simple-ira': [
    'contributionEligibility',
    'employeeContrib',
    'contribMethod',
    'pretaxSavingsRate',
    'preTaxSavingsAmt',
    'rothContribAllowed',
    'rothSavingsRate',
    'rothSavingsAmt',
    'posttaxContribAllowed',
    'posttaxSavingsRate',
    'postTaxSavingsAmt',
  ],
}

// Calculate the next step
export const accountTypeNextStep = {
  '401a': 'employer-contributions',
  '401k': 'employer-contributions',
  '403b': 'employer-contributions',
  '457': 'investments-intro',
  'keogh': 'employer-contributions',
  'payroll-deduct-ira': 'investments-intro',
  'sep-ira': 'investments-intro',
  'simple-ira': 'employer-contributions',
}

export const calculator = createDecorator(
  // Reset Contributing value if Eligibility is false
  {
    field: 'contributionEligibility',
    updates: {
      employeeContrib: (contributionEligibility, allValues, previousValues) => {
        if (contributionEligibility === false) return false
        // When eligibility flips back to true from a previously-false state,
        // clear the forced `false` so "Contributing now?" must be re-answered.
        // Otherwise the contribution-method toggle would reveal off a stale
        // false without an explicit answer. Guard on previousValues so the
        // initial form load (previousValues === undefined) preserves the value.
        if (previousValues && previousValues.contributionEligibility === false) return null
        return allValues.employeeContrib
      },
    },
  },

  // Reset Contribution method and pre-tax savings rate and amount when Contributing value is false
  {
    field: 'employeeContrib',
    updates: {
      contribMethod: (employeeContrib, allValues) =>
        employeeContrib === false && allValues.contribMethod === null
          ? 'P'
          : allValues.contribMethod,
      pretaxSavingsRate: (employeeContrib, allValues) =>
        employeeContrib === false ? 0 : allValues.pretaxSavingsRate,
      preTaxSavingsAmt: (employeeContrib, allValues) =>
        employeeContrib === false ? 0 : allValues.preTaxSavingsAmt,
    },
  },

  // Reset corresponding savings rates or amounts when the Contribution Method changes
  {
    field: 'contribMethod',
    updates: {
      pretaxSavingsRate: (contribMethod, allValues) =>
        contribMethod === 'D' ? 0 : allValues.pretaxSavingsRate,
      preTaxSavingsAmt: (contribMethod, allValues) =>
        contribMethod === 'P' ? 0 : allValues.preTaxSavingsAmt,
      rothSavingsRate: (contribMethod, allValues) =>
        contribMethod === 'D' ? 0 : allValues.rothSavingsRate,
      rothSavingsAmt: (contribMethod, allValues) =>
        contribMethod === 'P' ? 0 : allValues.rothSavingsAmt,
      posttaxSavingsRate: (contribMethod, allValues) =>
        contribMethod === 'D' ? 0 : allValues.posttaxSavingsRate,
      postTaxSavingsAmt: (contribMethod, allValues) =>
        contribMethod === 'P' ? 0 : allValues.postTaxSavingsAmt,
    },
  },

  // Set Roth savings rate and amount to zero when Roth is declared not allowed
  {
    field: 'rothContribAllowed',
    updates: {
      rothSavingsRate: (rothContribAllowedValue, allValues) =>
        rothContribAllowedValue === false ? 0 : allValues.rothSavingsRate,
      rothSavingsAmt: (rothContribAllowedValue, allValues) =>
        rothContribAllowedValue === false ? 0 : allValues.rothSavingsAmt,
    },
  },

  // Set after-tax savings rate and amount to zero when after-tax is declared not allowed
  {
    field: 'posttaxContribAllowed',
    updates: {
      posttaxSavingsRate: (posttaxContribAllowedValue, allValues) =>
        posttaxContribAllowedValue === false ? 0 : allValues.posttaxSavingsRate,
      postTaxSavingsAmt: (posttaxContribAllowedValue, allValues) =>
        posttaxContribAllowedValue === false ? 0 : allValues.postTaxSavingsAmt,
    },
  }
)

export const createEditable = ({
  accountType,
  planType,
  isPrimaryOwned,
  control,
  institutionalContribMethod,
}) => {
  const allowedKeys = accountTypeAllowedKeys[accountType]
  const is403b = planType === 2
  const is457 = planType === 4

  // Combination of account type specific rules, account-control API results, primary/spouse owner, special 457 and 403b cases
  const contributionEligibilityIsEditable =
    _.includes(allowedKeys, 'contributionEligibility') && control.paycheckContribution
  const employeeContribIsEditable =
    _.includes(allowedKeys, 'employeeContrib') && control.paycheckContribution
  const contribMethodIsEditable =
    _.includes(allowedKeys, 'contribMethod') &&
    !(isPrimaryOwned && (is403b || is457) && institutionalContribMethod)
  const pretaxSavingsRateIsEditable = _.includes(allowedKeys, 'pretaxSavingsRate')
  const preTaxSavingsAmtIsEditable = _.includes(allowedKeys, 'preTaxSavingsAmt')
  const rothContribAllowedIsEditable = _.includes(allowedKeys, 'rothContribAllowed')
  const rothSavingsRateIsEditable = _.includes(allowedKeys, 'rothSavingsRate')
  const rothSavingsAmtIsEditable = _.includes(allowedKeys, 'rothSavingsAmt')
  const posttaxContribAllowedIsEditable = _.includes(allowedKeys, 'posttaxContribAllowed')
  const posttaxSavingsRateIsEditable = _.includes(allowedKeys, 'posttaxSavingsRate')
  const postTaxSavingsAmtIsEditable = _.includes(allowedKeys, 'postTaxSavingsAmt')

  return {
    contributionEligibilityIsEditable,
    employeeContribIsEditable,
    contribMethodIsEditable,
    pretaxSavingsRateIsEditable,
    preTaxSavingsAmtIsEditable,
    rothContribAllowedIsEditable,
    rothSavingsRateIsEditable,
    rothSavingsAmtIsEditable,
    posttaxContribAllowedIsEditable,
    posttaxSavingsRateIsEditable,
    postTaxSavingsAmtIsEditable,
  }
}

// Initialize Form Values after fetching account information
export const initializeValues = ({
  editable,
  account,
  control,
  institutionalContribMethod,
  editDrawer,
}) => {
  let {
    contributionEligibility,
    contribMethod,
    pretaxSavingsRate,
    preTaxSavingsAmt,
    rothContribAllowed,
    rothSavingsRate,
    rothSavingsAmt,
    posttaxContribAllowed,
    posttaxSavingsRate,
    postTaxSavingsAmt,
    employeeContrib,
  } = account

  if (!control.paycheckContribution) {
    contributionEligibility = false
  }

  if (!editable.pretaxSavingsRateIsEditable || !pretaxSavingsRate) {
    pretaxSavingsRate = 0
  }

  if (!editable.preTaxSavingsAmtIsEditable || !preTaxSavingsAmt) {
    preTaxSavingsAmt = 0
  }

  if (!editable.rothContribAllowedIsEditable) {
    rothContribAllowed = false
  }

  if (!editable.rothSavingsRateIsEditable || !rothSavingsRate) {
    rothSavingsRate = 0
  }

  if (!editable.rothSavingsAmtIsEditable || !rothSavingsAmt) {
    rothSavingsAmt = 0
  }

  if (!editable.posttaxContribAllowedIsEditable) {
    posttaxContribAllowed = false
  }

  if (!editable.posttaxSavingsRateIsEditable || !posttaxSavingsRate) {
    posttaxSavingsRate = 0
  }

  if (!editable.postTaxSavingsAmtIsEditable || !postTaxSavingsAmt) {
    postTaxSavingsAmt = 0
  }

  return {
    contributionEligibility,
    employeeContrib: editDrawer ? employeeContrib : !control.paycheckContribution ? false : null,
    contribMethod: institutionalContribMethod === 'B' ? null : contribMethod,
    pretaxSavingsRate,
    preTaxSavingsAmt,
    rothContribAllowed,
    rothSavingsRate,
    rothSavingsAmt,
    posttaxContribAllowed,
    posttaxSavingsRate,
    postTaxSavingsAmt,
  }
}

// Whether to reveal the contribution-method (% of pay vs. fixed $) toggle.
// It appears once "Contributing now?" (employeeContrib) has been answered in
// the eligible state, regardless of Yes/No. Kept null until answered so a
// stale employeeContrib=false (e.g. left over from an ineligible excursion,
// which the calculator now clears) does not reveal the toggle prematurely.
export const showContribMethodToggle = values =>
  values.contributionEligibility === true && values.employeeContrib !== null

// Label for the contribution-method toggle, conditioned on whether the user is
// contributing now. The "No" wording asks about a hypothetical future start,
// and refers to the account owner: "you" for the participant, the spouse's
// name for a spouse-owned account.
export const contribMethodToggleLabel = (values, { participantName, isSpouseOwned } = {}) => {
  if (values.employeeContrib !== false) {
    return 'Are contributions a percentage of pay (%) or a fixed dollar amount?'
  }
  return isSpouseOwned
    ? `If ${participantName} starts contributing, would they contribute a percentage of pay (%) or a fixed dollar amount?`
    : 'If you start contributing, would you contribute a percentage of pay (%) or a fixed dollar amount?'
}

// This adds up the savings rates or amounts to see if we should display an error field
export const showZeroContributionsError = (editable, values, touched) => {
  const valuesToCheck = {
    pretaxSavingsRate: editable().pretaxSavingsRateIsEditable,
    rothSavingsRate: editable().rothSavingsRateIsEditable,
    posttaxSavingsRate: editable().posttaxSavingsRateIsEditable,
    preTaxSavingsAmt: editable().preTaxSavingsAmtIsEditable,
    rothSavingsAmt: editable().rothSavingsAmtIsEditable,
    postTaxSavingsAmt: editable().postTaxSavingsAmtIsEditable,
  }

  const percentTotal = values.pretaxSavingsRate + values.rothSavingsRate + values.posttaxSavingsRate
  const dollarTotal = values.preTaxSavingsAmt + values.rothSavingsAmt + values.postTaxSavingsAmt

  const showPercentError =
    percentTotal === 0 &&
    values.contribMethod === 'P' &&
    (valuesToCheck.pretaxSavingsRate ? touched.pretaxSavingsRate : true) &&
    (valuesToCheck.rothSavingsRate ? touched.rothSavingsRate : true) &&
    (valuesToCheck.posttaxSavingsRate ? touched.posttaxSavingsRate : true)

  const showDollarError =
    dollarTotal === 0 &&
    values.contribMethod === 'D' &&
    (valuesToCheck.preTaxSavingsAmt ? touched.preTaxSavingsAmt : true) &&
    (valuesToCheck.rothSavingsAmt ? touched.rothSavingsAmt : true) &&
    (valuesToCheck.postTaxSavingsAmt ? touched.postTaxSavingsAmt : true)

  return values.employeeContrib === true && (showPercentError || showDollarError)
}
